import { KeyboardEvent } from 'react';
import { Box, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '@callmart/types';
import { RowAckInfo, RowCompleteness } from './budgetCalculation';
import dayjs from '@utils/dayjs';

interface RowStatusCellProps {
    value?: RowCompleteness;
    /** ag-grid hands the whole row through — the Overview grid stamps `ackInfo` on it (CALL-816),
     *  and `orphaned` (CALL-820: working unit without active strategic volumes). */
    data?: { ackInfo?: RowAckInfo; orphaned?: boolean };
    /** CALL-818: opens the row's month-editor dialog (where CALL-816's acknowledgement also lives).
     *  Set via cellRendererParams by the Overview grid; absent on other grids. */
    onAckClick?: (data: Record<string, unknown>) => void;
    /** CALL-816: whether acknowledging is available there (draft + budget-write user) — drives the
     *  tooltip's hint wording; the icon opens the dialog as a viewer either way. */
    ackEnabled?: boolean;
    /** CALL-816: the funded period the counts below are over, e.g. "Dez 25 – Dez 27". Without it a
     *  period-wide "0 / 25" reads as if it were about the months on screen. */
    periodLabel?: string;
}

const RowStatusCell = ({ value, data, onAckClick, ackEnabled, periodLabel }: RowStatusCellProps) => {
    const { t } = useTranslation('budget');
    if (!value) return null;

    const { status, monthsWithCost, monthsTotal, missingPrice, missingAbt, missingVolume, missingBonus, flatFee } =
        value;

    const parts: string[] = [];
    if (flatFee) {
        if (missingBonus > 0) {
            parts.push(t('prices.missingBonusCount', { count: missingBonus }));
        }
    } else {
        if (missingPrice > 0) parts.push(t('prices.missingPriceCount', { count: missingPrice }));
        if (missingAbt > 0) parts.push(t('prices.missingAbtCount', { count: missingAbt }));
        if (missingVolume > 0) parts.push(t('prices.missingVolumeCount', { count: missingVolume }));
    }

    const ackInfo = data?.ackInfo;
    const acked = Boolean(ackInfo?.ack && ackInfo.covered);
    // CALL-820: the row's working unit has no active Handled-Contacts strategic volume left — reload
    // can never fill it (the Kairo case behind CALL-819). Its own icon, because the generic warning
    // reads as "months not filled in yet" when the real fact is "the data source is gone".
    const orphaned = Boolean(data?.orphaned);

    let stateLine: string;
    let icon;

    if (status === 'complete') {
        stateLine = t('prices.monthsComplete', { count: monthsWithCost, total: monthsTotal });
        icon = <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />;
    } else {
        stateLine =
            status === 'partial'
                ? t('prices.monthsPartial', { count: monthsWithCost, total: monthsTotal })
                : t('hint.noData');
        // CALL-816: an acknowledged warning keeps the triangle shape but turns green, wherever the
        // row would otherwise warn (yellow AND red) — a reviewer sees "accepted" on every window.
        if (acked) icon = <WarningAmberIcon fontSize="small" sx={{ color: 'success.main' }} />;
        else if (status === 'partial') icon = <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />;
        else icon = <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />;
    }

    // The orphan fact outranks the completeness shape (a complete row can still be orphaned — its
    // figures are frozen, nothing refreshes them): the icon KEEPS the LinkOff shape so the dead
    // source stays legible. A COVERED acknowledgement changes only the colour to green — same
    // shape-vs-colour split as the acknowledged warning triangle above: a row without a Strat source
    // can be deliberate (work ended and accepted; a manual-only budget line), and the ack is the
    // reviewed-and-accepted gesture the page offers. That green is self-correcting, not a mask: the
    // ack covers a NAMED set of missing months, so when the next reload empties more of a dead
    // working unit's months, coverage breaks and the red returns on its own.
    if (orphaned) {
        icon = <LinkOffIcon fontSize="small" sx={{ color: acked ? 'success.main' : 'error.main' }} />;
    }

    // CALL-818: the icon is the row's one entry into the month-editor dialog, which is ALSO where a
    // warning is acknowledged — so it is live in every state (complete rows and read-only versions
    // open a viewer), gated only on the acknowledgement data having loaded (see useWarningAcks: an
    // unloaded list must not let a confirm overwrite the original acknowledger).
    const clickable = Boolean(onAckClick && ackInfo);
    // What the click leads to, for the tooltip hint and the accessible name alike.
    const clickHint = !ackEnabled
        ? t('detail.clickToOpen')
        : acked
          ? t('ack.clickToManage')
          : ackInfo && ackInfo.canonicalMissing.length > 0
            ? t('ack.clickToAcknowledge')
            : t('detail.clickToOpen');

    // CALL-816: one line per fact rather than a single ' · ' run — by the time the tooltip carries the
    // verdict, the period it is counted over, up to three reasons and the acknowledgement, a sentence is
    // unreadable. Order answers the questions in the order they get asked: why is it green, what is the
    // state, over which months, what is missing.
    const lines = [
        ...(acked
            ? [
                  t('ack.acknowledged', {
                      name: ackInfo!.ack!.acknowledgedByName || '—',
                      date: dayjs(ackInfo!.ack!.updatedAt).format(DATE_FORMATS.DATE),
                  }),
              ]
            : []),
        // CALL-820: the explanation first — the counts below only say months are missing, this says why.
        ...(orphaned ? [t('hint.orphanedWorkingUnit')] : []),
        stateLine,
        // Every count above is over the funded budget period, not the view — so the period is named.
        ...(periodLabel ? [`${t('summary.budgetPeriod')} ${periodLabel}`] : []),
        ...parts,
        // A stale acknowledgement (months went missing that were never accepted) warns again, but the
        // tooltip says why the green went away.
        ...(!acked && ackInfo?.ack && status !== 'complete' ? [t('ack.outdated')] : []),
        // The icon's only other affordance is the cursor (double-clicking the row works too) — so
        // the tooltip has to advertise the click, or nobody finds the dialog behind it.
        ...(clickable ? [clickHint] : []),
    ];

    const openAckDialog = () => onAckClick!(data as Record<string, unknown>);

    return (
        <Tooltip
            arrow
            title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {lines.map((line, index) => (
                        // notranslate, not just a span: every line interpolates data (counts, a name, a
                        // date) and is rewritten in place as the row changes, which is what crashes React
                        // under a translation extension (CALL-759).
                        <span key={line} className="notranslate" style={index === 0 ? { fontWeight: 600 } : undefined}>
                            {line}
                        </span>
                    ))}
                </Box>
            }
        >
            <span
                style={{ display: 'inline-flex', alignItems: 'center', cursor: clickable ? 'pointer' : undefined }}
                {...(clickable && {
                    onClick: openAckDialog,
                    // The icon is the ONLY way into the dialog, so it has to answer the keyboard too:
                    // role="button" alone announces a button that does nothing on Enter/Space.
                    onKeyDown: (event: KeyboardEvent<HTMLSpanElement>) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        openAckDialog();
                    },
                    role: 'button',
                    tabIndex: 0,
                    // Names what the click DOES — same wording as the tooltip's hint line.
                    'aria-label': clickHint,
                })}
            >
                {icon}
            </span>
        </Tooltip>
    );
};

export default RowStatusCell;
